import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useApiClient, validateSuccess2xx } from '../../clients/apiAuth';
import ViewPerfil from './ViewPerfil';
import { CustomToast, ErrorToast, OkToast } from '../../components/specific/Toast';

const normalizePerfilData = (result) => {
  if (!result || typeof result !== 'object') return null;

  const resultData = result.data;
  if (Array.isArray(resultData)) return resultData[0] || null;
  if (resultData && typeof resultData === 'object') return resultData;

  return null;
};

const Perfil = ({ auth, authLogout }) => {
  const { get, post } = useApiClient();
  const history = useHistory();

  const authUser = auth?.data?.user || null;
  const userId = authUser?.TERCERO_ID;

  const [perfil, setPerfil] = useState(authUser);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [submittingPassword, setSubmittingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  useEffect(() => {
    let isMounted = true;

    const loadProfile = async () => {
      if (!userId) return;

      setLoadingProfile(true);
      try {
        const result = await get(`/tercero/${userId}`);
        const normalizedProfile = normalizePerfilData(result);
        if (isMounted && normalizedProfile) setPerfil(normalizedProfile);
      } catch (error) {
        if (isMounted) setPerfil(authUser || null);
      } finally {
        if (isMounted) setLoadingProfile(false);
      }
    };

    loadProfile();

    return () => {
      isMounted = false;
    };
    // `get` cambia de referencia en cada render y puede provocar llamadas en bucle.
    // Solo necesitamos recargar cuando cambie el usuario autenticado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const roles = useMemo(() => {
    if (Array.isArray(perfil?.rol)) return perfil.rol;
    if (Array.isArray(authUser?.rol)) return authUser.rol;
    return [];
  }, [authUser, perfil]);

  const handlePasswordChange = (field, value) => {
    setPasswordForm((prevState) => ({ ...prevState, [field]: value }));
  };

  const resetPasswordForm = () => {
    setPasswordForm({
      current_password: '',
      new_password: '',
      confirm_password: '',
    });
  };

  const submitPasswordChange = async (event) => {
    event.preventDefault();

    if (!userId) {
      ErrorToast('Error:', 'No se encontró el identificador del usuario.');
      return;
    }

    const { current_password, new_password, confirm_password } = passwordForm;
    if (!current_password || !new_password || !confirm_password) {
      ErrorToast('Error:', 'Todos los campos de contraseña son obligatorios.');
      return;
    }

    if (new_password !== confirm_password) {
      ErrorToast('Error:', 'La nueva contraseña y su confirmación no coinciden.');
      return;
    }

    setSubmittingPassword(true);
    try {
      const result = await post(
        `/tercero/${userId}/changePassword`,
        {
          json: JSON.stringify({
            current_password,
            new_password,
            confirm_password,
          }),
        },
        'application/json'
      );

      if (!validateSuccess2xx(result)) {
        CustomToast({ result });
        return;
      }

      OkToast({ message: result?.message || 'Contraseña actualizada correctamente.' });
      resetPasswordForm();
      authLogout();
      history.push('/');
    } catch (error) {
      ErrorToast('Error:', 'No se pudo actualizar la contraseña.');
    } finally {
      setSubmittingPassword(false);
    }
  };

  return (
    <ViewPerfil
      authUser={authUser}
      perfil={perfil}
      roles={roles}
      userId={userId}
      loadingProfile={loadingProfile}
      submittingPassword={submittingPassword}
      passwordForm={passwordForm}
      onPasswordChange={handlePasswordChange}
      onSubmitPasswordChange={submitPasswordChange}
    />
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

const mapDispatchToProps = (dispatch) => ({
  authLogout: () => dispatch({ type: 'LOG_OUT' }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Perfil);
